// The frame around the signed-in pages: the Relatude mark, the two sections, the theme, who is
// signed in and the way out, and a footer that says what this service is. The passkey nudge is
// here because it should follow the user until they act on it.
import type { ReactNode } from 'react'
import { useAuth } from '../auth'
import { navigate, useRoute } from '../router'
import { Logo } from './Logo'
import { Icon, Link, ThemeToggle } from './Ui'

export function Shell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const route = useRoute()
  const section = route.path.startsWith('/account') ? 'account' : 'licenses'

  async function logout() {
    await signOut()
    navigate('/')
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-inner">
          <Link to="/" className="brand" ariaLabel="Relatude.License: your licenses">
            <Logo height={22} />
            <span className="brand-product">License</span>
          </Link>
          <nav className="topnav" aria-label="Sections">
            <Link to="/" className={section === 'licenses' ? 'active' : ''}>
              Licenses
            </Link>
            <Link to="/account" className={section === 'account' ? 'active' : ''}>
              Account
            </Link>
          </nav>
          <div className="topbar-right">
            <ThemeToggle />
            <Link to="/account" className="topbar-user" title={user?.email || user?.mobile || ''}>
              <span className="avatar" aria-hidden="true">
                {initials(user?.displayName ?? '')}
              </span>
              <span className="topbar-name">{user?.displayName}</span>
            </Link>
            <button type="button" className="button button-quiet button-small topbar-signout" onClick={logout}>
              <Icon name="log-out" size={16} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </header>
      {user && user.passkeys.length === 0 && section !== 'account' && (
        <div className="nudge">
          <div className="nudge-inner">
            <Icon name="key" />
            <span>
              You have no passkey yet. A passkey signs you in with your fingerprint, face or device PIN, and cannot be phished.{' '}
              <Link to="/account">Add one on the account page</Link>.
            </span>
          </div>
        </div>
      )}
      <main className="content">{children}</main>
      <footer className="footer">
        <div className="footer-inner">
          <span>
            <strong>Relatude.License</strong> is the license service for Relatude.DB: licenses, API keys and sign-in for its installations.
          </span>
          <nav className="footer-links" aria-label="Relatude">
            <a href="https://relatude.com" target="_blank" rel="noreferrer">
              relatude.com
            </a>
            <a href="https://relatude.com/products/relatude-db" target="_blank" rel="noreferrer">
              Relatude.DB
            </a>
            <a href="https://db.relatude.com" target="_blank" rel="noreferrer">
              Documentation
            </a>
          </nav>
          <span>© {new Date().getFullYear()} Proventus AS</span>
        </div>
      </footer>
    </div>
  )
}

/** One or two letters for the avatar. A name gives its initials, an email its first two letters; a phone number has none, so a glyph. */
function initials(name: string): ReactNode {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters = words.length >= 2 ? words[0][0] + words[words.length - 1][0] : (words[0] ?? '').slice(0, 2)
  return /[a-z]/i.test(letters) ? letters.toUpperCase() : <Icon name="user" size={15} />
}
